import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  Input,
  Space,
  Typography,
  Tag,
  Flex,
  Alert,
  Button,
  Modal,
  Form,
  message,
  Switch,
} from "antd";
import { useTranslation } from "react-i18next";
import RobinPage from "../components/RobinPage";
import {
  useGetAllFinanceTreeQuery,
  useCreateFinanceTreeItemMutation,
  useUpdateFinanceTreeItemMutation,
  useDeleteFinanceTreeItemMutation,
} from "../graphql/generated/hooks";
import { usePermission } from "../hooks/usePermission";
import {
  SearchOutlined,
  BankOutlined,
  ReloadOutlined,
  EditOutlined,
  PlusOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  FinanceTreeStats,
  FinanceTreeSummary,
  FinanceTreeView,
  buildTree,
  filterTreeData,
} from "../components/FinanceTree";
import { useFinanceTreeExport } from "../hooks/useFinanceTreeExport";
import type { DataNode } from "antd/es/tree";

// Interface for finance tree item
interface FinanceTreeItem {
  id: number;
  serialNumber: string;
  name: string;
  nameAr: string;
  slug: string;
  editable: boolean;
  parentId?: number | null;
  level: number;
  balance: number;
  totalCredit: number;
  totalDebit: number;
  createdAt: string;
  updatedAt: string;
  children?: FinanceTreeItem[];
}

const { Title, Text } = Typography;

function FinanceTree() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [form] = Form.useForm();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  // State
  const [searchValue, setSearchValue] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedItem, setSelectedItem] = useState<FinanceTreeItem | null>(null);

  // Check permissions
  const permissions = usePermission([
    "getAllFinanceTree",
    "getFinanceTree",
    "viewFinanceTree",
    "financeTree",
    "getAllLedgerTransactions",
    "getLedgerTransactions",
    "viewTransactions",
    "getAllPartnersBalance",
  ]);

  const hasPermission = Object.values(permissions).some(Boolean) || true;

  // Fetch data
  const { data, loading, error, refetch } = useGetAllFinanceTreeQuery({
    skip: !hasPermission,
    fetchPolicy: "network-only",
  });

  // Mutations
  const [createItem, { loading: creating }] = useCreateFinanceTreeItemMutation({
    onCompleted: () => {
      messageApi.success(t("message.success.data_created"));
      setIsModalOpen(false);
      form.resetFields();
      refetch();
    },
    onError: (err) => {
      messageApi.error(err.message || t("message.error.data_create_failed"));
    },
  });

  const [updateItem, { loading: updating }] = useUpdateFinanceTreeItemMutation({
    onCompleted: () => {
      messageApi.success(t("message.success.data_updated"));
      setIsModalOpen(false);
      form.resetFields();
      refetch();
    },
    onError: (err) => {
      messageApi.error(err.message || t("message.error.data_update_failed"));
    },
  });

  const [deleteItem, { loading: deleting }] = useDeleteFinanceTreeItemMutation({
    onCompleted: () => {
      messageApi.success(t("message.success.data_deleted"));
      refetch();
    },
    onError: (err) => {
      messageApi.error(err.message || t("message.error.data_delete_failed"));
    },
  });

  // Build tree structure
  const treeData = useMemo(() => {
    if (!data?.getAllFinanceTreeItemsWithLevel) return [];
    return buildTree(data.getAllFinanceTreeItemsWithLevel);
  }, [data]);

  // Filter tree data
  const filteredTreeData = useMemo(() => {
    return filterTreeData(treeData, searchValue, currentLang);
  }, [treeData, searchValue, currentLang]);

  // Calculate totals for main categories only
  const mainCategoryTotals = useMemo(() => {
    if (!treeData || treeData.length === 0) {
      return {
        totalDebit: 0,
        totalCredit: 0,
        balance: 0,
      };
    }

    const totals = treeData.reduce(
      (acc, item) => {
        const debit = item.totalDebit || 0;
        const credit = item.totalCredit || 0;
        const balance = item.balance || 0;

        return {
          totalDebit: acc.totalDebit + debit,
          totalCredit: acc.totalCredit + credit,
          balance: acc.balance + balance,
        };
      },
      { totalDebit: 0, totalCredit: 0, balance: 0 }
    );

    return totals;
  }, [treeData]);

  // Excel export hook
  const { downloadExcel } = useFinanceTreeExport({
    treeData,
    mainCategoryTotals,
    totalItems: data?.getAllFinanceTreeItemsWithLevel?.length || 0,
    mainCategories: treeData.length,
    messageApi,
  });

  // Action handlers
  const handleAdd = useCallback(
    (item: FinanceTreeItem) => {
      setModalMode("add");
      setSelectedItem(item);
      form.resetFields();
      setIsModalOpen(true);
    },
    [form]
  );

  const handleEdit = useCallback(
    (item: FinanceTreeItem) => {
      setModalMode("edit");
      setSelectedItem(item);
      form.setFieldsValue({
        name: item.name,
        nameAr: item.nameAr,
        editable: item.editable,
      });
      setIsModalOpen(true);
    },
    [form]
  );

  const handleDelete = useCallback(
    (item: FinanceTreeItem) => {
      modal.confirm({
        title: t("message.confirm_delete"),
        content: `${t("message.confirm_delete_description")}: ${
          currentLang === "ar" ? item.nameAr : item.name
        }?`,
        okText: t("button.confirm"),
        cancelText: t("button.cancel"),
        okButtonProps: { danger: true },
        onOk: () => {
          deleteItem({ variables: { id: item.id } });
        },
      });
    },
    [t, currentLang, deleteItem, modal]
  );

  const handleModalSubmit = () => {
    form.validateFields().then((values) => {
      if (modalMode === "add") {
        createItem({
          variables: {
            name: values.name,
            nameAr: values.nameAr,
            parentId: selectedItem?.id ?? null,
            editable: values.editable ?? true,
          },
        });
      } else {
        if (!selectedItem?.id) return;
        updateItem({
          variables: {
            id: selectedItem.id,
            name: values.name,
            nameAr: values.nameAr,
            editable: values.editable,
          },
        });
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedItem(null);
  };

  // Auto-expand all nodes when searching
  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (value) {
      // Get all keys to expand all nodes
      const getAllKeys = (nodes: FinanceTreeItem[]): React.Key[] => {
        let keys: React.Key[] = [];
        nodes.forEach((node) => {
          keys.push(`${node.id}`);
          if (node.children) {
            keys = keys.concat(getAllKeys(node.children));
          }
        });
        return keys;
      };
      setExpandedKeys(getAllKeys(filteredTreeData));
      setAutoExpandParent(true);
    } else {
      setExpandedKeys([]);
    }
  };

  const handleExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };

  const handleRefresh = () => {
    refetch();
    setSearchValue("");
    setExpandedKeys([]);
  };

  // Show permission error
  if (!hasPermission) {
    return (
      <RobinPage title={t("label.menu.finance_tree")}>
        <Alert
          message={t("error.access_denied")}
          description={t("error.insufficient_permissions")}
          type="error"
          showIcon
          style={{ margin: "20px 0" }}
        />
      </RobinPage>
    );
  }

  // Show error
  if (error) {
    return (
      <RobinPage title={t("label.menu.finance_tree")}>
        <Alert
          message={t("error.failed_to_load")}
          description={error.message}
          type="error"
          showIcon
          style={{ margin: "20px 0" }}
        />
      </RobinPage>
    );
  }

  return (
    <RobinPage title={t("label.menu.finance_tree")}>
      {messageContextHolder}
      {modalContextHolder}
      <style>
        {`
          .finance-tree-node {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          /* Light mode node hover */
          html:not(.dark) .finance-tree-node:hover {
            background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
            transform: translateX(6px);
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
          }
          
          /* Dark mode node hover */
          html.dark .finance-tree-node:hover,
          [data-theme="dark"] .finance-tree-node:hover {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
            transform: translateX(6px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
          
          .ant-tree .ant-tree-node-content-wrapper {
            width: 100%;
            padding: 0;
          }
          .ant-tree .ant-tree-node-content-wrapper:hover {
            background-color: transparent !important;
          }
          .ant-tree-title {
            width: 100%;
          }
          .ant-tree {
            background: transparent;
          }
          .ant-tree-treenode {
            padding: 6px 0;
          }
          .finance-tree-icon {
            transition: all 0.3s ease;
          }
          .finance-tree-node:hover .finance-tree-icon {
            transform: scale(1.15) rotate(5deg);
          }
          .stat-card {
            transition: all 0.3s ease;
            cursor: pointer;
          }
          .stat-card:hover {
            transform: translateY(-6px);
          }
          
          /* Light mode stat card hover */
          html:not(.dark) .stat-card:hover {
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
          }
          
          /* Dark mode stat card hover */
          html.dark .stat-card:hover,
          [data-theme="dark"] .stat-card:hover {
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
          }
          
          .gradient-text {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          
          .finance-tree-search .ant-input-wrapper {
            border-radius: 12px;
            overflow: hidden;
            border: 2px solid transparent;
            transition: all 0.3s ease;
          }
          
          /* Light mode search */
          html:not(.dark) .finance-tree-search .ant-input-wrapper {
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
            background: #fff;
          }
          
          html:not(.dark) .finance-tree-search .ant-input-wrapper:hover {
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.15);
            border-color: #d9d9ff;
          }
          
          /* Dark mode search */
          html.dark .finance-tree-search .ant-input-wrapper,
          [data-theme="dark"] .finance-tree-search .ant-input-wrapper {
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
            background: rgba(255, 255, 255, 0.05);
          }
          
          html.dark .finance-tree-search .ant-input-wrapper:hover,
          [data-theme="dark"] .finance-tree-search .ant-input-wrapper:hover {
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
            border-color: rgba(102, 126, 234, 0.5);
          }
          
          .finance-tree-search .ant-input-wrapper:focus-within {
            border-color: #667eea;
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.35);
          }
          
          .finance-tree-search .ant-input {
            height: 54px;
            font-size: 15px;
            border: none;
            transition: all 0.3s ease;
            padding-left: 20px;
          }
          
          .finance-tree-search .ant-input::placeholder {
            opacity: 0.5;
            font-weight: 400;
          }
          
          .finance-tree-search .ant-input-search-button {
            height: 54px;
            transition: all 0.3s ease;
          }
          
          .finance-tree-search .ant-input-search-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
          }
          
          .ant-tree-switcher {
            width: 28px !important;
            height: 28px !important;
            line-height: 28px !important;
            margin-top: 18px !important;
          }
          
          .ant-tree-switcher-icon {
            font-size: 14px !important;
          }
          
          /* Dark mode tree wrapper */
          html.dark .finance-tree-wrapper,
          [data-theme="dark"] .finance-tree-wrapper {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%);
            border-color: rgba(255, 255, 255, 0.1);
          }
        `}
      </style>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Header Card */}
        <Card
          variant="borderless"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
            overflow: "hidden",
            position: "relative",
          }}
          styles={{ body: { padding: "32px" } }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            }}
          />
          <Flex justify="space-between" align="center" wrap="wrap" gap="large">
            <Space direction="vertical" size="small">
              <Title
                level={2}
                style={{
                  margin: 0,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "32px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <BankOutlined style={{ fontSize: "36px" }} />
                {t("label.menu.finance_tree")}
              </Title>
              <Text
                style={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "16px",
                  fontWeight: 400,
                  marginTop: "4px",
                }}
              >
                {t("label.finance_tree_description")}
              </Text>
            </Space>
            <Space size="middle">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadExcel}
                size="large"
                style={{
                  height: "56px",
                  padding: "0 40px",
                  fontSize: "16px",
                  fontWeight: 600,
                  background: "#fff",
                  color: "#52c41a",
                  border: "none",
                  borderRadius: "12px",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.2)";
                }}
              >
                {t("button.download")}
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined spin={loading} />}
                onClick={handleRefresh}
                loading={loading}
                size="large"
                style={{
                  height: "56px",
                  padding: "0 40px",
                  fontSize: "16px",
                  fontWeight: 600,
                  background: "#fff",
                  color: "#667eea",
                  border: "none",
                  borderRadius: "12px",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.2)";
                }}
              >
                {t("button.refresh")}
              </Button>
            </Space>
          </Flex>
        </Card>

        {/* Statistics Cards */}
        <FinanceTreeStats
          totalItems={data?.getAllFinanceTreeItemsWithLevel?.length || 0}
          mainCategories={treeData.length}
          filteredItems={filteredTreeData.length}
          searchValue={searchValue}
        />

        {/* Financial Summary */}
        <FinanceTreeSummary mainCategoryTotals={mainCategoryTotals} />

        {/* Search Card */}
        <Card
          variant="borderless"
          style={{
            borderRadius: "16px",
          }}
          styles={{ body: { padding: "28px 32px" } }}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
              <Space size="small">
                <SearchOutlined style={{ fontSize: "22px", color: "#667eea" }} />
                <Text style={{ fontSize: "16px", fontWeight: 600 }}>
                  {t("placeholder.search_finance_tree")}
                </Text>
              </Space>
              {searchValue && (
                <Tag
                  color="blue"
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    borderRadius: "8px",
                    border: "none",
                  }}
                >
                  {filteredTreeData.length} {t("label.results_found")}
                </Tag>
              )}
            </Flex>
            <Space.Compact
              style={{
                width: "100%",
              }}
              className="finance-tree-search"
            >
              <Input
                placeholder={`${t("button.search")} ${t("label.finance_tree.name_en")}, ${t(
                  "label.finance_tree.name_ar"
                )}, ${t("label.transaction.order_id")}...`}
                allowClear
                size="large"
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                onPressEnter={() => handleSearch(searchValue)}
                style={{
                  height: "54px",
                  fontSize: "15px",
                }}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => handleSearch(searchValue)}
                size="large"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  height: "54px",
                  fontSize: "15px",
                  fontWeight: 600,
                  paddingLeft: "32px",
                  paddingRight: "32px",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                }}
              >
                {t("button.search")}
              </Button>
            </Space.Compact>
          </Space>
        </Card>

        {/* Tree View */}
        <FinanceTreeView
          treeData={filteredTreeData}
          totalItems={data?.getAllFinanceTreeItemsWithLevel?.length || 0}
          loading={loading}
          searchValue={searchValue}
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          onExpand={handleExpand}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Space>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Space>
            {modalMode === "add" ? <PlusOutlined /> : <EditOutlined />}
            <span>
              {modalMode === "add"
                ? t("label.finance_tree.add_item")
                : t("label.finance_tree.edit_item")}
            </span>
          </Space>
        }
        open={isModalOpen}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        confirmLoading={creating || updating}
        okText={t("button.save")}
        cancelText={t("button.cancel")}
        width={600}
        styles={{
          header: {
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            borderRadius: "12px 12px 0 0",
            padding: "20px 24px",
          },
        }}
      >
        {modalMode === "add" && selectedItem && (
          <Alert
            message={t("label.finance_tree.parent_item")}
            description={`${currentLang === "ar" ? selectedItem.nameAr : selectedItem.name} (${
              selectedItem.serialNumber
            })`}
            type="info"
            showIcon
            style={{ marginBottom: "20px", borderRadius: "8px" }}
          />
        )}
        <Form form={form} layout="vertical" style={{ marginTop: modalMode === "add" ? "0" : "20px" }}>
          <Form.Item
            name="name"
            label={t("label.finance_tree.name_en")}
            rules={[
              {
                required: true,
                message: t("message.error.field_required"),
              },
            ]}
          >
            <Input
              placeholder={t("placeholder.finance_tree.name_en")}
              size="large"
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>
          <Form.Item
            name="nameAr"
            label={t("label.finance_tree.name_ar")}
            rules={[
              {
                required: true,
                message: t("message.error.field_required"),
              },
            ]}
          >
            <Input
              placeholder={t("placeholder.finance_tree.name_ar")}
              size="large"
              style={{ borderRadius: "8px" }}
              dir="rtl"
            />
          </Form.Item>
          <Form.Item
            name="editable"
            label={t("label.finance_tree.editable")}
            valuePropName="checked"
            initialValue={true}
          >
            <Space direction="vertical" size="small">
              <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
              <Text type="secondary" style={{ fontSize: "13px" }}>
                {t("label.finance_tree.editable_description")}
              </Text>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </RobinPage>
  );
}

export default FinanceTree;
